<template>
  <v-dialog v-model="dialog" width="680" content-class="buyer-details-dialog">
    <v-card class="buyer-details-card">
      <!------------------------------------------HEADER------------------------------------------>
      <div class="buyer-details-header">
        <div class="buyer-details-header__text">
          <div class="buyer-details-header__title">
            {{ $t("buyer_details.title") }}
          </div>
          <div v-if="subtitle" class="buyer-details-header__subtitle">
            {{ subtitle }}
          </div>
        </div>
        <button
          type="button"
          class="buyer-details-header__close"
          @click="dialog = false"
        >
          <v-icon color="white" size="20">mdi-close</v-icon>
        </button>
      </div>

      <div class="buyer-details-body">
        <!----------------------------------------ACQUIRENTE--------------------------------------->
        <section class="buyer-details-section">
          <div class="buyer-details-section__header">
            <div class="buyer-details-section__label">
              <v-icon size="18" color="#c62828">mdi-account</v-icon>
              <span>{{ $t("buyer_details.buyer") }}</span>
            </div>
            <div class="buyer-details-section__actions">
              <span
                class="buyer-details-badge"
                :class="
                  isRegistered
                    ? 'buyer-details-badge--registered'
                    : 'buyer-details-badge--unregistered'
                "
              >
                {{
                  isRegistered
                    ? $t("buyer_details.registered")
                    : $t("buyer_details.unregistered")
                }}
              </span>
              <template v-if="!hideContact">
                <v-btn
                  v-if="!isContacted"
                  :loading="loading"
                  size="small"
                  color="#c62828"
                  class="buyer-details-btn buyer-details-btn--primary"
                  @click="contactBuyer"
                >
                  <v-icon start size="16">mdi-email-outline</v-icon>
                  {{ $t("buyer_details.contact_client") }}
                </v-btn>
                <v-btn
                  v-else
                  size="small"
                  variant="outlined"
                  color="success"
                  class="buyer-details-btn"
                  disabled
                >
                  {{ $t("buyer_details.contacted") }}
                  <v-icon end size="16">mdi-check</v-icon>
                </v-btn>
              </template>
            </div>
          </div>

          <div class="buyer-details-grid">
            <div class="buyer-details-field">
              <span class="buyer-details-field__label">
                {{ $t("buyer_details.full_name") }}
              </span>
              <span class="buyer-details-field__value">
                {{ buyerFullName || "—" }}
              </span>
            </div>

            <div class="buyer-details-field">
              <span class="buyer-details-field__label">
                {{ $t("buyer_details.phone") }}
              </span>
              <div class="buyer-details-field__row">
                <a
                  v-if="buyerPhone"
                  :href="`tel:${buyerPhone}`"
                  class="buyer-details-phone"
                >
                  {{ buyerPhone }}
                </a>
                <span v-else class="buyer-details-field__value">—</span>
                <button
                  v-if="buyerPhone"
                  type="button"
                  class="buyer-details-copy"
                  @click="copyText(buyerPhone)"
                >
                  <v-icon size="16">mdi-content-copy</v-icon>
                </button>
              </div>
            </div>

            <div class="buyer-details-field">
              <span class="buyer-details-field__label">
                {{ $t("buyer_details.email") }}
              </span>
              <div class="buyer-details-field__row">
                <a
                  v-if="buyerEmail"
                  :href="`mailto:${buyerEmail}`"
                  class="buyer-details-email"
                >
                  {{ buyerEmail }}
                </a>
                <span v-else class="buyer-details-field__value">—</span>
                <button
                  v-if="buyerEmail"
                  type="button"
                  class="buyer-details-copy"
                  @click="copyText(buyerEmail)"
                >
                  <v-icon size="16">mdi-content-copy</v-icon>
                </button>
              </div>
            </div>

            <div class="buyer-details-field">
              <span class="buyer-details-field__label">
                {{ $t("buyer_details.message") }}
              </span>
              <span class="buyer-details-field__value">
                {{ buyerMessage || "—" }}
              </span>
            </div>

            <div class="buyer-details-field buyer-details-field--full">
              <span class="buyer-details-field__label">
                {{ $t("buyer_details.address") }}
              </span>
              <span class="buyer-details-field__value">
                {{ buyerAddress || "—" }}
              </span>
            </div>

            <div class="buyer-details-field">
              <span class="buyer-details-field__label">
                {{ $t("buyer_details.city") }}
              </span>
              <span class="buyer-details-field__value">
                {{ buyerCity || "—" }}
              </span>
            </div>

            <div class="buyer-details-field">
              <span class="buyer-details-field__label">
                {{ $t("buyer_details.postal_code") }}
              </span>
              <span class="buyer-details-field__value">
                {{ buyerPostalCode || "—" }}
              </span>
            </div>
          </div>
        </section>

        <!------------------------------------DROP SHIPPING------------------------------------>
        <section v-if="hasDropShipping" class="buyer-details-section">
          <div class="buyer-details-divider" />
          <div class="buyer-details-section__header">
            <div class="buyer-details-section__label">
              <v-icon size="18" color="#c62828">mdi-truck-delivery</v-icon>
              <span>{{ $t("buyer_details.drop_shipping_recipient") }}</span>
            </div>
            <img
              :src="scotchRrBadge"
              alt="Usa Scotch RR per dropshipping"
              class="scotch-rr-badge"
            />
          </div>

          <div class="buyer-details-grid">
            <div class="buyer-details-field">
              <span class="buyer-details-field__label">
                {{ $t("buyer_details.full_name") }}
              </span>
              <span class="buyer-details-field__value">
                {{ dropShippingFullName || "—" }}
              </span>
            </div>

            <div class="buyer-details-field">
              <span class="buyer-details-field__label">
                {{ $t("buyer_details.phone") }}
              </span>
              <div class="buyer-details-field__row">
                <a
                  v-if="dropShipping.phone"
                  :href="`tel:${dropShipping.phone}`"
                  class="buyer-details-phone"
                >
                  {{ dropShipping.phone }}
                </a>
                <span v-else class="buyer-details-field__value">—</span>
                <button
                  v-if="dropShipping.phone"
                  type="button"
                  class="buyer-details-copy"
                  @click="copyText(dropShipping.phone)"
                >
                  <v-icon size="16">mdi-content-copy</v-icon>
                </button>
              </div>
            </div>

            <div class="buyer-details-field">
              <span class="buyer-details-field__label">
                {{ $t("buyer_details.email") }}
              </span>
              <div class="buyer-details-field__row">
                <a
                  v-if="dropShipping.email"
                  :href="`mailto:${dropShipping.email}`"
                  class="buyer-details-email"
                >
                  {{ dropShipping.email }}
                </a>
                <span v-else class="buyer-details-field__value">—</span>
                <button
                  v-if="dropShipping.email"
                  type="button"
                  class="buyer-details-copy"
                  @click="copyText(dropShipping.email)"
                >
                  <v-icon size="16">mdi-content-copy</v-icon>
                </button>
              </div>
            </div>

            <div class="buyer-details-field">
              <span class="buyer-details-field__label">
                {{ $t("buyer_details.city") }}
              </span>
              <span class="buyer-details-field__value">
                {{ dropShipping.city || "—" }}
              </span>
            </div>

            <div class="buyer-details-field">
              <span class="buyer-details-field__label">
                {{ $t("buyer_details.postal_code") }}
              </span>
              <span class="buyer-details-field__value">
                {{ dropShipping.postal_code || "—" }}
              </span>
            </div>

            <div class="buyer-details-field buyer-details-field--full">
              <span class="buyer-details-field__label">
                {{ $t("buyer_details.address") }}
              </span>
              <span class="buyer-details-field__value">
                {{ dropShipping.address || "—" }}
              </span>
            </div>
          </div>
        </section>
      </div>

      <!------------------------------------------ACTIONS------------------------------------------>
      <div class="buyer-details-footer">
        <v-btn
          variant="outlined"
          color="#c62828"
          class="buyer-details-btn"
          @click="print"
        >
          <v-icon start size="18">mdi-printer</v-icon>
          {{ $t("buyer_details.print") }}
        </v-btn>
      </div>
    </v-card>

    <v-snackbar v-model="copied" color="success" timeout="1500" location="top">
      {{ $t("buyer_details.copied") }}
    </v-snackbar>
  </v-dialog>
</template>

<script>
import { Printd } from "printd";
import putRequest from "../api/putRequest";
import scotchRrBadge from "@/assets/ScotchRR_PropShipping.png";

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function printField(label, value) {
  const display = value && String(value).trim() ? String(value) : "—";
  return `
    <div class="field">
      <div class="label">${escapeHtml(label)}</div>
      <div class="value">${escapeHtml(display)}</div>
    </div>
  `;
}

function printDetails(payload) {
  const {
    title,
    subtitle,
    buyerTitle,
    dropShippingTitle,
    buyer,
    dropShipping,
    labels,
    hasDropShipping,
  } = payload;

  const dropShippingHtml = hasDropShipping
    ? `
      <div class="section">
        <div class="section-title">${escapeHtml(dropShippingTitle)}</div>
        <div class="grid">
          ${printField(labels.fullName, dropShipping.fullName)}
          ${printField(labels.phone, dropShipping.phone)}
          ${printField(labels.email, dropShipping.email)}
          ${printField(labels.city, dropShipping.city)}
          ${printField(labels.postalCode, dropShipping.postalCode)}
          ${printField(labels.address, dropShipping.address)}
        </div>
      </div>
    `
    : "";

  const content = document.createElement("div");
  content.setAttribute("id", "content");
  content.innerHTML = `
    <div class="main">
      <div class="header">
        <div class="title">${escapeHtml(title)}</div>
        ${
          subtitle
            ? `<div class="subtitle">${escapeHtml(subtitle)}</div>`
            : ""
        }
      </div>
      <div class="section">
        <div class="section-title">${escapeHtml(buyerTitle)}</div>
        <div class="grid">
          ${printField(labels.fullName, buyer.fullName)}
          ${printField(labels.phone, buyer.phone)}
          ${printField(labels.email, buyer.email)}
          ${printField(labels.message, buyer.message)}
          ${printField(labels.address, buyer.address)}
          ${printField(labels.city, buyer.city)}
          ${printField(labels.postalCode, buyer.postalCode)}
        </div>
      </div>
      ${dropShippingHtml}
    </div>
  `;

  const css = [
    `
    .main {
      font-family: Arial, Helvetica, sans-serif;
      color: #212121;
      max-width: 700px;
      margin: 0 auto;
      padding: 12px;
    }
    .header {
      border-bottom: 2px solid #c62828;
      padding-bottom: 10px;
      margin-bottom: 18px;
    }
    .title {
      font-size: 20px;
      font-weight: 700;
    }
    .subtitle {
      margin-top: 4px;
      font-size: 13px;
      color: #616161;
    }
    .section {
      margin-bottom: 18px;
    }
    .section-title {
      font-size: 13px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: #c62828;
      margin-bottom: 10px;
    }
    .grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px 16px;
    }
    .field .label {
      font-size: 11px;
      color: #757575;
      margin-bottom: 2px;
    }
    .field .value {
      font-size: 14px;
      font-weight: 700;
      word-break: break-word;
    }
    @media print {
      .main { padding: 0; }
    }
  `,
  ];

  const d = new Printd();
  return d.print(content, css);
}

function joinName(name, surname) {
  return [name, surname].filter(Boolean).join(" ").trim();
}

export default {
  props: {
    hideContact: { type: Boolean, default: false },
  },
  data: () => ({
    dialog: false,
    loading: false,
    copied: false,
    buyerDetails: {},
    orderId: null,
    scotchRrBadge,
  }),
  computed: {
    dropShipping() {
      return this.buyerDetails?.drop_shipping || {};
    },
    hasDropShipping() {
      const ds = this.dropShipping;
      return Boolean(
        ds.name ||
          ds.surname ||
          ds.email ||
          ds.phone ||
          ds.address ||
          ds.city ||
          ds.postal_code
      );
    },
    checkout() {
      return this.buyerDetails?.checkout_information || {};
    },
    buyer() {
      return this.buyerDetails?.buyerDetails || {};
    },
    buyerFullName() {
      return (
        joinName(this.checkout.name, this.checkout.surname) ||
        joinName(this.buyer.name, this.buyer.surname)
      );
    },
    buyerEmail() {
      return this.checkout.email || this.buyer.email || "";
    },
    buyerPhone() {
      return this.checkout.phone || this.buyer.phone || "";
    },
    buyerAddress() {
      return this.checkout.address || this.buyer.address || "";
    },
    buyerCity() {
      return this.checkout.city || "";
    },
    buyerPostalCode() {
      return this.checkout.postal_code || "";
    },
    buyerMessage() {
      return this.checkout.message || "";
    },
    dropShippingFullName() {
      return joinName(this.dropShipping.name, this.dropShipping.surname);
    },
    isRegistered() {
      return Boolean(this.buyer.registered);
    },
    isContacted() {
      return Boolean(this.buyer.contacted || this.buyerDetails.contacted);
    },
    subtitle() {
      const parts = (this.buyerDetails?.meta || []).filter(Boolean);
      return parts.join(" · ");
    },
  },
  watch: {
    dialog(value) {
      if (value === false) {
        this.buyerDetails = {};
        this.orderId = null;
      }
    },
  },
  methods: {
    showBuyerDetails(data) {
      this.buyerDetails = data || {};
      this.orderId = data?.id || null;
      this.dialog = true;
    },
    copyText(text) {
      if (!text) return;
      navigator.clipboard.writeText(text).then(() => {
        this.copied = true;
      });
    },
    print() {
      printDetails({
        title: this.$t("buyer_details.title"),
        subtitle: this.subtitle,
        buyerTitle: this.$t("buyer_details.buyer"),
        dropShippingTitle: this.$t("buyer_details.drop_shipping_recipient"),
        hasDropShipping: this.hasDropShipping,
        labels: {
          fullName: this.$t("buyer_details.full_name"),
          phone: this.$t("buyer_details.phone"),
          email: this.$t("buyer_details.email"),
          message: this.$t("buyer_details.message"),
          address: this.$t("buyer_details.address"),
          city: this.$t("buyer_details.city"),
          postalCode: this.$t("buyer_details.postal_code"),
        },
        buyer: {
          fullName: this.buyerFullName,
          phone: this.buyerPhone,
          email: this.buyerEmail,
          message: this.buyerMessage,
          address: this.buyerAddress,
          city: this.buyerCity,
          postalCode: this.buyerPostalCode,
        },
        dropShipping: {
          fullName: this.dropShippingFullName,
          phone: this.dropShipping.phone,
          email: this.dropShipping.email,
          address: this.dropShipping.address,
          city: this.dropShipping.city,
          postalCode: this.dropShipping.postal_code,
        },
      });
    },
    contactBuyer() {
      this.loading = true;
      putRequest("/order/contact-buyer/", `order_id=${this.orderId}`)
        .then(() => {
          this.loading = false;
          if (this.buyerDetails.buyerDetails) {
            this.buyerDetails.buyerDetails.contacted = true;
          }
          this.buyerDetails.contacted = true;
        })
        .catch(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.buyer-details-card {
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18) !important;
}

.buyer-details-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  background: #c62828;
  color: #fff;
  padding: 16px 18px;
}

.buyer-details-header__title {
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.2;
}

.buyer-details-header__subtitle {
  margin-top: 4px;
  font-size: 0.85rem;
  opacity: 0.92;
}

.buyer-details-header__close {
  width: 28px;
  height: 28px;
  border: 1.5px solid rgba(255, 255, 255, 0.85);
  border-radius: 50%;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
}

.buyer-details-body {
  padding: 18px 20px 8px;
}

.buyer-details-section + .buyer-details-section {
  margin-top: 4px;
}

.buyer-details-section__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

.buyer-details-section__label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #c62828;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.buyer-details-section__actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.buyer-details-badge {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 0.72rem;
  font-weight: 600;
}

.buyer-details-badge--unregistered {
  background: #fde8e8;
  color: #c62828;
}

.buyer-details-badge--registered {
  background: #e8f5e9;
  color: #2e7d32;
}

.buyer-details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5px 20px;
}

.buyer-details-field--full {
  grid-column: 1 / -1;
}

.buyer-details-field__label {
  display: block;
  font-size: 0.72rem;
  color: #9e9e9e;
  margin-bottom: 2px;
}

.buyer-details-field__value {
  display: block;
  font-size: 0.95rem;
  font-weight: 700;
  color: #212121;
  word-break: break-word;
}

.buyer-details-field__row {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.buyer-details-email {
  color: #1565c0;
  font-weight: 700;
  font-size: 0.95rem;
  text-decoration: none;
  word-break: break-all;
}

.buyer-details-email:hover {
  text-decoration: underline;
}

.buyer-details-phone {
  color: #212121;
  font-weight: 700;
  font-size: 0.95rem;
  text-decoration: none;
}

.buyer-details-phone:hover {
  text-decoration: underline;
}

.buyer-details-copy {
  border: 0;
  background: transparent;
  color: #9e9e9e;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
}

.buyer-details-copy:hover {
  color: #616161;
}

.buyer-details-divider {
  height: 1px;
  background: #ececec;
  margin: 16px 0;
}

.scotch-rr-badge {
  height: 52px;
  width: auto;
  max-width: 220px;
  display: block;
  object-fit: contain;
  flex-shrink: 0;
}

.buyer-details-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  padding: 12px 18px 18px;
  flex-wrap: wrap;
}

.buyer-details-btn {
  text-transform: uppercase !important;
  letter-spacing: 0.03em;
  font-weight: 700 !important;
}

.buyer-details-btn--primary {
  color: #fff !important;
}

@media (max-width: 600px) {
  .buyer-details-grid {
    grid-template-columns: 1fr;
  }
}
</style>
